import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Eye, EyeOff } from 'lucide-react';
import { authService } from '../services/AuthService';
import { AnalyticsEvents, track } from '../services/AnalyticsService';

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.acceptInvite({ email, token, password });
      track(AnalyticsEvents.InviteAccepted);
      navigate('/login');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Något gick fel');
    }
    setLoading(false);
  };

  if (!token || !email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-700 mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ogiltig länk</h1>
          <p className="text-gray-500 mb-6">Inbjudningslänken saknar nödvändig information. Kontrollera att du klickade på rätt länk i e-postmeddelandet.</p>
          <Link to="/login" className="text-teal-700 hover:text-teal-800 font-medium">Gå till inloggning</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-700 mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Välkommen till MinVägförening</h1>
          <p className="text-gray-500 mt-1">Välj ett lösenord för att slutföra din registrering</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 mb-1.5">E-postadress</label>
            <input
              id="invite-email"
              type="email"
              value={email}
              readOnly
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="invite-password" className="block text-sm font-medium text-gray-700 mb-1.5">Välj lösenord</label>
            <div className="relative">
              <input
                id="invite-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={10}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all pr-10"
                placeholder="Minst 10 tecken"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-teal-700 hover:bg-teal-800 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Slutför registrering...' : 'Slutför registrering'}
          </button>

          <p className="text-center text-sm text-gray-500">
            Har du redan ett konto?{' '}
            <Link to="/login" className="text-teal-700 hover:text-teal-800 font-medium">
              Logga in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
